import { Injectable } from '@angular/core';
import { ConnectService, IConnectContact } from '@epicuro-next/data/connect';
import { ConnectionStatus } from '@epicuro-next/models/users';
import { ConnectNotificationType } from '@epicuro-next/platform/configuration';
import * as fromSignalrState from '@epicuro-next/state/signalr-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import * as actions from '../actions';
import {
  selectIncomingCall,
  selectUserCurrentContactState,
} from '../selectors';

@Injectable()
export class ConnectTwilioEffects {
  constructor(
    private actions$: Actions,
    private connectService: ConnectService,
    private store: Store,
  ) {}

  public initializeCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.initializeCallSuccess),
      map(() => actions.createVideoCall()),
    ),
  );

  public createVideoCallRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createVideoCall),
      switchMap(() => [actions.loadTwilioToken(), actions.loadRoom()]),
    ),
  );

  public loadTwilioToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadTwilioToken),
      switchMap(() =>
        this.connectService.acquireTwilioToken().pipe(
          map((token) => actions.loadTwilioTokenSuccess({ token })),
          catchError((error) =>
            of(actions.loadTwilioTokenFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public loadTwilioTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadTwilioTokenFailure),
      map(() =>
        showSnackBarMessage({
          message: $localize`:connect|Failed to load call@@failedToLoadRoom:Failed to load the support call`,
          hideDelay: 10000,
          messageType: MessageType.DANGER,
        }),
      ),
    ),
  );

  public loadRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadRoom),
      switchMap(() =>
        this.connectService.loadRoom().pipe(
          map((room) => actions.loadRoomSuccess({ room })),
          catchError((error) =>
            of(actions.loadRoomFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public loadRoomSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadRoomSuccess),
      withLatestFrom(this.store.pipe(select(selectUserCurrentContactState))),
      switchMap(([{ room }, contact]) => [
        fromSignalrState.sendSignalrMessage({
          methodName: ConnectNotificationType.InitiateVideoCall,
          params: [contact?.userId, room.name],
        }),
        actions.listenSocketCallDeclineMessage(),
      ]),
    ),
  );

  public loadRoomFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadRoomFailure),
      map(() =>
        showSnackBarMessage({
          message: $localize`:connect|Failed to load room@@failedToLoadRoom:Failed to load the support room`,
          hideDelay: 10000,
          messageType: MessageType.DANGER,
        }),
      ),
    ),
  );

  public declineIncomingCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.declineIncomingCall),
      withLatestFrom(this.store.pipe(select(selectIncomingCall))),
      switchMap(([{ reason }, incomingCall]) => [
        fromSignalrState.sendSignalrMessage({
          methodName: ConnectNotificationType.DeclineVideoCall,
          params: [
            incomingCall?.contact.userId,
            incomingCall?.roomName,
            reason,
          ],
        }),
        actions.removeIncomingCall(),
      ]),
    ),
  );

  public acceptIncomingCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.acceptIncomingCall),
      withLatestFrom(this.store.pipe(select(selectIncomingCall))),
      switchMap(([_, incomingCall]) => [
        actions.incomingCallAccepted({
          contact: incomingCall?.contact as IConnectContact,
        }),
        actions.initializeCall(),
        actions.setRoomNameState({
          roomName: incomingCall?.roomName as string,
        }),
        actions.loadTwilioToken(),
        actions.setUserConnectionStatus({
          status: ConnectionStatus.ONCALL,
        }),
      ]),
    ),
  );
}
